import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setCookie } from '../../utils/CookieUtils';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import { history } from '../history';
import { getUserLogin } from '../../ApiServices/AuthServices';
import { getProfile } from './GetUserProfileSlice';
import { clearStoreState } from './storesSlice';
import { clearMessagesState, fetchNewMessageCount } from './messagesSlice';
import { clearProductState } from './productsSlice';
import { t } from 'i18next';
import { fetchDropdownData } from './dropdownDataSlice';
import { fetchNewOrderCount } from './ordersSlice';

export interface Authorization {
  userName: string;
  password: string;
}

export interface AuthState {
  auth: Authorization;
  loading: boolean;
  error: string;
}

// initialize state from local storage to enable user to stay logged in
function createInitialState() {
  let savedAuth = localStorage.getItem('auth');
  return savedAuth === null
    ? { auth: { userName: '', password: '' }, loading: false, error: '' }
    : { value: JSON.parse(savedAuth) };
}

// saving state data
function createReducers() {
  return { setAuth };

  function setAuth(state: any, action: any) {
    state.value = action?.payload;
  }
}

// implementation - methods without api calls
function createExtraActions() {
  return { logout: logout() };

  function logout() {
    return createAsyncThunk('logout', function(arg, { dispatch }) {
      dispatch(authActions.setAuth(undefined));
      localStorage.removeItem('auth');
      localStorage.removeItem('stores');
      history.navigate('/login');
    });
  }
}

const getUser = createAsyncThunk(
  'authorization/getUser',
  async (payload: Authorization, { dispatch }) => {
    try {
      dispatch(showLoader({ message: t('AUTHENTICATING'), isLoading: true }));

      let response = await getUserLogin(payload);
      let user = response?.data;

      //set auth user in redux state
      dispatch(authActions.setAuth(user));

      dispatch(clearStoreState());
      dispatch(clearProductState());
      dispatch(clearMessagesState());

      dispatch(getProfile(''));
      dispatch(fetchDropdownData());
      dispatch(fetchNewMessageCount(''));
      dispatch(fetchNewOrderCount(''));

      dispatch(
        showSuccessSnackbar({
          message: t('LOGGED_IN_SUCCESSFULLY')
        })
      );

      setCookie('supplier_token', user?.userDetails?.token);
      localStorage.setItem('auth', JSON.stringify(user));

      return user;
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
    } finally {
      dispatch(hideLoader());
    }
  }
);

const authorizationSlice = createSlice({
  name: 'authorization',
  initialState: createInitialState,
  reducers: createReducers
});

export default authorizationSlice.reducer;
export const authState = (state: RootState) => state?.auth;
export const authActions = { ...authorizationSlice.actions, ...createExtraActions(), getUser };
